import type { LegacyManageCampaign } from '@/lib/manage-screen-adapters';

/** One flight as the campaign builder edits it: calendar days as `YYYY-MM-DD`
 *  and the budget in whole dollars. It is the legacy adapter's flight shape, so
 *  a loaded campaign's flights drop straight into the editor. */
export type CampaignFlight = LegacyManageCampaign['flights'][number];

/** The builder field a schedule issue is reported against. */
export type FlightIssueKey =
  | 'campaign-start-date'
  | 'campaign-end-date'
  | 'campaign-budget'
  | 'campaign-flights';

export interface FlightIssue {
  key: FlightIssueKey;
  message: string;
}

export interface FlightValidationOptions {
  /** The start date the server holds, which an aged draft may keep. */
  storedStartDate?: string | null;
  /** Today in New York, `YYYY-MM-DD`. */
  todayStr?: string | null;
  /** The latest end date the server accepts, `YYYY-MM-DD`. */
  maxEndISO?: string | null;
}

export function flightValidationIssues(
  flights: readonly CampaignFlight[],
  startDate: string,
  endDate: string,
  totalBudget: number,
  { storedStartDate = null, todayStr = null, maxEndISO = null }: FlightValidationOptions = {},
): FlightIssue[] {
  const issues: FlightIssue[] = [];
  const s = new Date(startDate).getTime();
  const e = new Date(endDate).getTime();
  if (isNaN(s)) {
    issues.push({ key: 'campaign-start-date', message: 'Select a valid campaign start date.' });
  }
  if (isNaN(e)) {
    issues.push({ key: 'campaign-end-date', message: 'Select a valid campaign end date.' });
  } else if (!isNaN(s) && e < s) {
    issues.push({ key: 'campaign-end-date', message: 'End date must be on or after the start date.' });
  }
  // The API rejects a start date in the past, but only when the request MOVES it
  // — an aged draft keeps its original start. `min` on the input is a hint the
  // wizard never validated, so the check has to live here or it 4xxs on save.
  if (todayStr && startDate && startDate < todayStr && startDate !== storedStartDate) {
    issues.push({
      key: 'campaign-start-date',
      message: 'Start date cannot be in the past.',
    });
  }
  if (maxEndISO && endDate && endDate > maxEndISO) {
    issues.push({
      key: 'campaign-end-date',
      message: 'Campaigns can be planned up to 180 days ahead.',
    });
  }
  if (!Number.isFinite(Number(totalBudget)) || Number(totalBudget) <= 0) {
    issues.push({ key: 'campaign-budget', message: 'Enter a total budget greater than zero.' });
  }
  if (!flights || flights.length === 0) {
    issues.push({ key: 'campaign-flights', message: 'Add at least one flight.' });
    return issues;
  }

  const sorted = [...flights].sort((a,b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  for (const f of sorted) {
    const fs = new Date(f.start).getTime();
    const fe = new Date(f.end).getTime();
    if (isNaN(fs) || isNaN(fe)) {
      issues.push({ key: 'campaign-flights', message: `${f.name || 'A flight'} needs valid start and end dates.` });
      break;
    }
    if ((!isNaN(s) && fs < s) || (!isNaN(e) && fe > e)) {
      issues.push({ key: 'campaign-flights', message: `${f.name || 'A flight'} must stay inside the campaign dates.` });
      break;
    }
    if (fe < fs) {
      issues.push({ key: 'campaign-flights', message: `${f.name || 'A flight'} must end on or after it starts.` });
      break;
    }
    if (!Number.isFinite(Number(f.budget)) || Number(f.budget) <= 0) {
      issues.push({ key: 'campaign-flights', message: `${f.name || 'A flight'} needs a budget greater than zero.` });
      break;
    }
  }
  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i];
    const previous = sorted[i - 1];
    if (!current || !previous) continue;
    if (new Date(current.start).getTime() <= new Date(previous.end).getTime()) {
      issues.push({
        key: 'campaign-flights',
        message: `${current.name || 'A flight'} overlaps ${previous.name || 'the previous flight'}.`,
      });
      break;
    }
  }
  const sum = sorted.reduce((acc, f) => acc + (+f.budget || 0), 0);
  if (sum !== Number(totalBudget)) {
    issues.push({
      key: 'campaign-flights',
      message: `Flight budgets must add up to the $${Number(totalBudget || 0).toLocaleString()} total budget.`,
    });
  }
  return issues;
}

/**
 * Keep a single untouched flight in step with the campaign's own dates and budget.
 *
 * A new campaign starts with one flight covering the whole range and holding the
 * whole budget. Editing the campaign's dates or budget used to leave that flight
 * behind, so the operator immediately failed validation — "flight budgets must
 * add up to the total" — for a flight they had never touched, and had to go fix
 * a number the wizard had just invalidated on their behalf.
 *
 * Deliberately narrow. It fires ONLY when nothing has been edited and there is
 * exactly one flight, which is the case where "the flight" and "the campaign"
 * mean the same thing and mirroring cannot lose information. With several
 * flights, or any flight the operator has touched, their intent is real and this
 * does nothing — redistributing someone's split silently would be worse than the
 * validation error it saves.
 *
 * Returns the SAME array when nothing needs to change, so it is safe to run from
 * an effect whose dependency is that array.
 */
export function mirrorSoleUntouchedFlight(
  flights: CampaignFlight[],
  editedFlights: ReadonlySet<string>,
  startDate: string,
  endDate: string,
  totalBudget: number,
): CampaignFlight[] {
  if (editedFlights.size > 0 || flights.length !== 1) return flights;
  if (!startDate || !endDate) return flights;
  const [only] = flights;
  if (!only) return flights;
  const budget = Number(totalBudget);
  if (!Number.isFinite(budget) || budget <= 0) return flights;
  if (only.start === startDate && only.end === endDate && Number(only.budget) === budget) {
    return flights;
  }
  return [{ ...only, start: startDate, end: endDate, budget }];
}

export function flightsValid(
  flights: readonly CampaignFlight[],
  startDate: string,
  endDate: string,
  totalBudget: number,
): boolean {
  return flightValidationIssues(flights, startDate, endDate, totalBudget).length === 0;
}
